import { Button } from '../ui/button';
import { Edit } from 'lucide-react';
import { formatDate, cn } from '../../lib/utils';

function LeaveCard({ label, value, total, color, mode = 'balance' }) {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  const colorClasses = {
    indigo: 'bg-indigo-500',
    red: 'bg-red-500',
    purple: 'bg-purple-500',
    slate: 'bg-slate-500',
    emerald: 'bg-emerald-500',
  };

  return (
    <div className="p-4 rounded-xl bg-secondary/30">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold mt-1">
        {value}{' '}
        <span className="text-sm font-normal text-muted-foreground">
          {mode === 'taken' ? 'days taken' : `/ ${total} days`}
        </span>
      </p>
      {total > 0 && mode === 'balance' && (
        <div className="mt-2 h-2 bg-secondary rounded-full overflow-hidden">
          <div className={cn('h-full rounded-full', colorClasses[color])} style={{ width: `${percentage}%` }} />
        </div>
      )}
    </div>
  );
}

/**
 * Leave Balance tab for the Employee Detail page. Pure presentation —
 * the edit action delegates back to the parent via `onEdit`.
 */
export function EmployeeLeaveTab({ employee, canEdit, onEdit }) {
  return (
    <div className="glass-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Leave Balance</h3>
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            data-testid="edit-leave-balance-btn"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Balances
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <LeaveCard label="Annual Leave" value={employee.leave_balance?.annual || 0} total={21} color="indigo" />
        <LeaveCard label="Sick Leave" value={employee.leave_balance?.sick || 0} total={30} color="red" />
        <LeaveCard label="Family Responsibility Leave" value={employee.leave_balance?.study || 0} total={3} color="purple" />
        <LeaveCard label="Unpaid Leave" value={employee.leave_balance?.unpaid || 0} total={0} color="slate" />
        <LeaveCard label="Public Holiday Leave" value={employee.leave_balance?.public_holiday || 0} mode="taken" color="emerald" />
      </div>

      <div className="mt-6 pt-6 border-t border-border/40">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Accrual Settings
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="p-3 rounded-lg bg-secondary/30">
            <p className="text-xs text-muted-foreground">Annual Accrual Start</p>
            <p className="font-medium mt-1" data-testid="accrual-start-display">
              {employee.annual_leave_accrual_start
                ? formatDate(employee.annual_leave_accrual_start)
                : <span className="text-muted-foreground italic">Not set</span>}
            </p>
            <p className="text-xs text-muted-foreground mt-1">1.25 days/month</p>
          </div>
          <div className="p-3 rounded-lg bg-secondary/30">
            <p className="text-xs text-muted-foreground">Last Annual Accrual Run</p>
            <p className="font-medium mt-1">
              {employee.last_annual_accrual_run
                ? formatDate(employee.last_annual_accrual_run)
                : <span className="text-muted-foreground italic">—</span>}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-secondary/30">
            <p className="text-xs text-muted-foreground">Sick Cycle Start</p>
            <p className="font-medium mt-1" data-testid="sick-cycle-start-display">
              {employee.sick_cycle_start
                ? formatDate(employee.sick_cycle_start)
                : <span className="text-muted-foreground italic">Not set</span>}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Resets 30 days / 3 years</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Edit anchor dates on the employee&apos;s main edit page. Accruals run automatically each day at 00:05 SAST.
        </p>
      </div>
    </div>
  );
}
